import { t } from "@lingui/core/macro";
import {
  LoaderFunctionArgs,
  redirect,
  ShouldRevalidateFunctionArgs,
  useMatch,
  useNavigate,
  useRouteLoaderData,
} from "react-router";
import dayjs from "dayjs";

import { User, useUser } from "./user";
import { AuthService } from "../auth";
import { createApiClient } from "../api-client";
import { getIntent } from "../data-routing";
import { FeatureFlagService } from "../feature-flag-service";
import { MediumDTO } from "../dto-schemas/energy-management";
import { makeMediumFromDTO, Medium } from "../../features/ems/domain";

export type Member = Pick<User, "id" | "email" | "name">;
export type Organization = {
  id: string;
  name: string;
  edgeDevices: Array<string>;
  activeLicenses: Array<string>;
  fiscalYearStartMonth?: number;
};

export type Client = {
  clientId: string;
  clientSecret: string;
  name: string;
  createdAt: dayjs.Dayjs;
};

export async function activeOrganizationLoader({ params }: LoaderFunctionArgs) {
  const apiClient = createApiClient(AuthService.getTokenSilently, params.orgaId);
  const canManageOrgas = FeatureFlagService.isEnabled("permissions/organization-management");

  const orgaId = params.orgaId!;
  let orgaToActivate = (await apiClient.getOrganizations()).find((orga) => orga.id === orgaId);
  if (!orgaToActivate && canManageOrgas) {
    try {
      orgaToActivate = await apiClient.getOrganizationById(orgaId);
    } catch {
      // this is intentionally left empty: navigate to /
    }
  }

  if (orgaToActivate) {
    const user = await AuthService.user();
    if (user) {
      localStorage.setItem(user.id, JSON.stringify({ activeOrganizationId: orgaToActivate.id }));
    }
    const media = await apiClient.getMedia();
    return { activeOrganization: orgaToActivate, media };
  } else {
    return redirect("/");
  }
}

export function activeOrganizationChanged({
  currentParams,
  currentUrl: { pathname },
  defaultShouldRevalidate,
  formAction,
  json,
  nextParams,
}: ShouldRevalidateFunctionArgs): boolean {
  const intent = getIntent(json);
  const switchedOrganization = currentParams.orgaId !== nextParams.orgaId;
  const updatedOrganization =
    pathname.startsWith(`/${currentParams.orgaId}/settings`) &&
    (intent === "renameOrganization" || intent === "changeFiscalYearStartMonth");
  const deletedOrganization =
    pathname.startsWith(`/${currentParams.orgaId}/settings`) && intent === "deleteOrganization";
  const changedMedia = formAction?.endsWith("/settings/media") ?? false;

  if (switchedOrganization || updatedOrganization || changedMedia || deletedOrganization) {
    return defaultShouldRevalidate;
  }
  return false;
}

export function useOrganization() {
  const navigate = useNavigate();
  const { organizations } = useUser();
  const activeOrganization = useRouteLoaderData("organizationId").activeOrganization as
    Organization | undefined;
  const match = useMatch(`/${activeOrganization?.id}/:app/*`);

  function setActiveOrganization(organizationId: string): void {
    const suffix = match?.params.app ?? "home";
    navigate(`/${organizationId}/${suffix}`);
  }

  return { organizations, activeOrganization, setActiveOrganization };
}

export function organizationName(organization?: Pick<Organization, "name">): string {
  return organization?.name || t`Unnamed Workspace`;
}

export function useMedia(): {
  availableMedia: ReadonlyArray<Medium>;
} {
  const media = useRouteLoaderData("organizationId").media as Array<MediumDTO>;

  return {
    availableMedia: media.map(makeMediumFromDTO),
  };
}
